import React from 'react';
import PropTypes from 'prop-types';
import {ToastContainer, toast} from 'react-toastify';
import {WarningToast} from './WarningToast';
import {ErrorToast} from './ErrorToast';
import {CloseButton} from './CloseButton';
import 'react-toastify/dist/ReactToastify.css';
import className from './style.module.scss';

const ToasterContext = React.createContext(null);
const Types = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  DARK: 'dark',
};

/**
 * Toaster:
 * Is a wrapper over react-toastify. It takes a list of toasts
 * and render them from list. Once rendered they are removed.
 */
function Component(props) {
  const closeToast = (toastId) => () => {
    props.removeToast(toastId);
  };

  const displayAllToasts = () => {
    for (let toastItem of props.toastList) {
      switch (toastItem.type) {
        case Types.INFO:
          toast.info(toastItem.message, {
            onClose: closeToast(toastItem.id),
          });
          break;
        case Types.SUCCESS:
          toast.success(toastItem.message, {
            onClose: closeToast(toastItem.id),
          });
          break;
        case Types.WARNING:
          toast.warning(
            <WarningToast.Body
              title={toastItem.title}
              message={toastItem.message}
              image={toastItem.image}
            />,
            {
              onClose: closeToast(toastItem.id),
              className: WarningToast.className,
              bodyClassName: WarningToast.bodyClassName,
              progressClassName: WarningToast.progressClassName,
            }
          );
          break;
        case Types.ERROR:
          toast.error(
            <ErrorToast.Body
              title={toastItem.title}
              message={toastItem.message}
            />,
            {
              onClose: closeToast(toastItem.id),
              className: ErrorToast.className,
              bodyClassName: ErrorToast.bodyClassName,
              progressClassName: ErrorToast.progressClassName,
            }
          );
          break;
        case Types.DARK:
          toast.dark(toastItem.message, {
            onClose: closeToast(toastItem.id),
          });
          break;
        default:
          toast(toastItem.message, {onClose: closeToast(toastItem.id)});
      }
    }
  };

  React.useEffect(() => {
    toast.dismiss();
    displayAllToasts();
  }, [props.toastList]);

  return (
    <ToasterContext.Provider value={props.addToast}>
      {props.children}
      <ToastContainer
        className={className.toastContainer}
        closeButton={CloseButton}
        position={props.position}
        autoClose={props.autoClose}
        hideProgressBar={props.hideProgressBar}
        newestOnTop={props.newestOnTop}
        closeOnClick={props.closeOnClick}
        rtl={props.rtl}
        pauseOnFocusLoss={props.pauseOnFocusLoss}
        draggable={props.draggable}
        pauseOnHover={props.pauseOnHover}
      />
    </ToasterContext.Provider>
  );
}

Component.propTypes = {
  ...ToastContainer.propTypes,
  /**
   * List of toasts to be rendered
   */
  toastList: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Type of toast
       */
      type: PropTypes.oneOf([
        'dark',
        'info',
        'error',
        'success',
        'warning',
        '',
      ]),
      /**
       * message to display on toster
       */
      message: PropTypes.string,
      /**
       * UniqueId for toaster
       */
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  /**
   * Function to add a toast to the toastList
   */
  addToast: PropTypes.func,
  /**
   * Funtion to remove a toaster once it has been displayed
   */
  removeToast: PropTypes.func,
  /**
   * Child componet that could use the toaster
   */
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.element]),
};

Component.defaultProps = {
  toastList: [],
  position: 'top-center',
  autoClose: 2500,
  hideProgressBar: false,
  newestOnTop: true,
  closeOnClick: true,
  rtl: false,
  draggable: false,
  pauseOnHover: true,
  pauseOnFocusLoss: false,
};

export const Toaster = {
  Component,
  Context: ToasterContext,
  Types,
};
